/**
 * Bounded polling of an AI grading job.
 *
 * SSE is the primary channel; this is the fallback for a client that cannot hold
 * a stream. It reads the same authoritative status from the `Job` table, so the
 * two paths cannot disagree.
 *
 * The bound is real, and it matters: the attempt row is already persisted
 * server-side, so a client that keeps polling forever is not protecting anything
 * — it is just holding a student on a spinner past the point where the worker
 * could plausibly still be running.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { JobStatus } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';

/** Tight enough to feel instant while the worker is realistically still working. */
const FAST_INTERVAL_MS = 2_000;
/** Past this point the job is slow, not imminent; back off to save requests. */
const FAST_WINDOW_MS = 30_000;
const SLOW_INTERVAL_MS = 5_000;
/**
 * Give up after four minutes. The worker's own ceiling is two BullMQ attempts
 * over a four-attempt Gemini ladder; past this the job is wedged, and the honest
 * thing is to say so rather than spin indefinitely.
 */
const DEADLINE_MS = 240_000;

export interface GradingPollerState {
  data: JobStatus | undefined;
  /** True once polling was abandoned without the job reaching a terminal state. */
  timedOut: boolean;
}

export function useGradingPoller(jobId: string | null): GradingPollerState {
  const startedAt = useRef(0);
  const timedJobId = useRef<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  // Restart the clock whenever a different job is handed over — including a swap
  // straight from one job to the next, with no null in between.
  if (jobId !== timedJobId.current) {
    timedJobId.current = jobId;
    startedAt.current = jobId ? Date.now() : 0;
  }

  const query = useQuery({
    queryKey: ['job', jobId],
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      if (state === 'completed' || state === 'failed') return false;
      const elapsed = Date.now() - startedAt.current;
      if (elapsed >= DEADLINE_MS) return false;
      return elapsed >= FAST_WINDOW_MS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS;
    },
    queryFn: () => apiGet<JobStatus>(`/jobs/${jobId}`),
  });

  const state = query.data?.state;
  const settled = state === 'completed' || state === 'failed';

  // Fire the flag on a timer rather than off a poll response, so a job that stops
  // answering entirely still surfaces instead of hanging silently.
  useEffect(() => {
    if (!jobId || settled) {
      setTimedOut(false);
      return;
    }
    const remaining = Math.max(0, startedAt.current + DEADLINE_MS - Date.now());
    const id = window.setTimeout(() => setTimedOut(true), remaining);
    return () => window.clearTimeout(id);
  }, [jobId, settled]);

  return { data: query.data, timedOut };
}
